import { Button, Stack, Typography, useTheme } from "@mui/material";
import { FileText, Lock } from "lucide-react";
import type { FC } from "react";
import { ALLOCATION_MAX, ALLOCATION_MIN } from "web_myplann/Goals/constants";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import type { GoalPortfolioListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

type Props = {
	portfolio: GoalPortfolioListItemResponseDTO;
	pinnedAllocation: number | null;
	hasDivider: boolean;
	onSelect: (portfolio: GoalPortfolioListItemResponseDTO) => void;
};

export const PinExistingContractItem: FC<Props> = ({
	portfolio,
	pinnedAllocation,
	hasDivider,
	onSelect,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const currencySign = t(Resources.Goals.Amount.currencySign);

	const isPinnedHere = pinnedAllocation !== null;
	const available = Math.max(0, ALLOCATION_MAX - portfolio.usagePercent);
	const isFull = available < ALLOCATION_MIN && !isPinnedHere;

	return (
		<Button
			variant="deprecatedListRow"
			disableRipple
			disabled={isFull}
			onClick={() => onSelect(portfolio)}
			sx={{
				borderBottom: hasDivider ? 1 : 0,
				borderColor: "surface.border",
				opacity: isFull ? 0.4 : 1,
			}}
		>
			<IconBox bg={theme.palette.grey[100]}>
				<FileText size={theme.iconSize.md} color={theme.palette.grey[500]} />
			</IconBox>
			<Stack flex={1} minWidth={0} textAlign="left">
				<Typography variant="bodyMedium" noWrap>
					{portfolio.title ?? ""}
				</Typography>
				<Typography variant="caption" color={theme.palette.grey[500]} noWrap>
					{[portfolio.producer, portfolio.domain].filter(Boolean).join(" - ")}
				</Typography>
				<Stack mt={0.5}>
					{isFull ? (
						<Stack
							direction="row"
							alignItems="center"
							gap={0.4}
							color={theme.palette.error.main}
						>
							<Lock size={12} />
							<Typography variant="caption" color="inherit">
								{t(
									Resources.Goals.Detail.Solution.Pin.Existing.fullyAllocated,
									{ percent: available, min: ALLOCATION_MIN },
								)}
							</Typography>
						</Stack>
					) : isPinnedHere ? (
						<Typography
							variant="caption"
							color={theme.palette.primary.main}
							tabular
						>
							{t(Resources.Goals.Detail.Solution.Pin.Existing.alreadyPinned, {
								percent: pinnedAllocation,
							})}
						</Typography>
					) : (
						<Typography
							variant="caption"
							color={theme.palette.grey[500]}
							tabular
						>
							{t(Resources.Goals.Detail.Solution.Pin.Existing.available, {
								percent: available,
							})}
						</Typography>
					)}
				</Stack>
			</Stack>
			<Stack textAlign="right" flexShrink={0}>
				<Typography variant="bodyMedium" tabular>
					{formatAmount(portfolio.portfolioValue ?? 0)} {currencySign}
				</Typography>
			</Stack>
		</Button>
	);
};
